/* eslint-disable no-shadow */
import { toObjectByKey } from '@fifa-bsm/bo-frontend-shared/utils';
import { appConfigurationApi } from '@/app/shared/services/apis/appConfigurationApi.service';
import { isFeatureFlagEnabled } from '@fifa-bsm/bo-frontend-shared/core';
import { apiClient } from '@/app/core/services/apiClient.service';

const entityToRequestMap = {
    countries: () => apiClient.get('countries').then((response) => response.data),
    languages: () => apiClient.get('languages').then((response) => response.data),
    guestOperationsGenders: () => apiClient.get('guest-operations-individuals/genders'),
    guestOperationsProtocols: () =>
        apiClient
            .get('guest-operations-individuals/protocols')
            .then((response) =>
                response.map((protocol) => ({ ...protocol, name: protocol.name.toString() }))
            ),
    guestOperationsStatuses: () => apiClient.get('guest-operations-individuals/statuses'),
    guestOperationsContactTypes: () => apiClient.get('guest-operations-individuals/contact-types'),
    timezones: () => import('@/assets/timezones/timezones.json').then((module) => module.default),
};

const pendingEntityPromises = {};

const resolveMissingEntity = (entityName) => {
    if (pendingEntityPromises[entityName]) {
        return pendingEntityPromises[entityName];
    }

    return entityToRequestMap[entityName]().then((data) => {
        pendingEntityPromises[entityName] = null;
        return { data, entityName };
    });
};

const initialState = {
    entities: {
        countries: null,
        languages: null,
        acrFunctions: null,
        guestOperationsGenders: null,
        guestOperationsProtocols: null,
        guestOperationsStatuses: null,
        guestOperationsContactTypes: null,
        timezones: null,
    },
    featureFlags: {},
    uiVisibility: {
        header: true,
    },
};

const state = () => ({ ...initialState });

const getters = {
    entities(state) {
        return state.entities;
    },
    countries(state) {
        return state.entities.countries ?? [];
    },
    languages(state) {
        return state.entities.languages ?? [];
    },
    acrFunctions(state) {
        return state.entities.acrFunctions ?? [];
    },
    guestOperationsGenders(state) {
        return state.entities.guestOperationsGenders ?? [];
    },
    guestOperationsProtocols(state) {
        return state.entities.guestOperationsProtocols ?? [];
    },
    guestOperationsStatuses(state) {
        return state.entities.guestOperationsStatuses ?? [];
    },
    guestOperationsContactTypes(state) {
        return state.entities.guestOperationsContactTypes ?? [];
    },
    countriesMap(state) {
        return toObjectByKey(state.entities.countries ?? [], 'id');
    },
    timezones(state) {
        return state.entities.timezones ?? [];
    },
    enabledFeatures(state) {
        return Object.entries(state.featureFlags).reduce((acc, [key, value]) => {
            if (isFeatureFlagEnabled(value)) {
                acc[key] = true;
            }
            return acc;
        }, {});
    },
};

const actions = {
    async initialize({ dispatch }) {
        dispatch('Routing/loadSelectedMainEvent', undefined, { root: true });
        await Promise.all([
            dispatch('loadEntities', {
                entities: ['countries', 'timezones'],
            }),
        ]);
    },
    async loadEntities({ state, commit }, { entities, forceReload = false }) {
        const missingEntities = forceReload
            ? entities
            : entities.filter((entityName) => !state.entities[entityName]);

        const resolvedEntities = await Promise.all(
            missingEntities.map((entityName) => resolveMissingEntity(entityName))
        );

        if (resolvedEntities.length > 0) {
            commit('setEntities', resolvedEntities);
        }
    },
    async loadFeatureFlags({ commit }, { onParseError } = {}) {
        const featureFlags = await appConfigurationApi.getFeatureFlags();
        const featureFlagsMap = featureFlags.reduce((map, featureFlag) => {
            try {
                map[featureFlag.name] = JSON.parse(featureFlag.value);
            } catch (error) {
                onParseError?.(featureFlag, error);
            }

            return map;
        }, {});

        commit('setFeatureFlags', featureFlagsMap);
    },
};

const mutations = {
    setEntities(state, entities) {
        const newEntities = entities.reduce((map, { entityName, data }) => {
            map[entityName] = Object.freeze(data.map((item) => Object.freeze(item)));
            return map;
        }, {});

        state.entities = {
            ...state.entities,
            ...newEntities,
        };
    },
    updateUiVisibility(state, uiFlags) {
        state.uiVisibility = {
            ...state.uiVisibility,
            ...uiFlags,
        };
    },
    setFeatureFlags(state, featureFlags) {
        state.featureFlags = featureFlags;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
